import React, { useEffect, useState } from "react";
import { useLoaderData, Link, useParams, useLocation } from "react-router-dom";
import {
  getParseStatusLabelById,
  getSystemStatusLabelById,
} from "utils/labels";
import {
  getSystemStatusColorByLabel,
  getParseStatusColorByLabel,
} from "utils/colors";
import cutStrAtAndAddDots from "utils/cutStrAtAndAddDots";

import { price2StringPrice } from "utils/priceformatter";
import CommonChip from "Components/UI/Tables/CommonChip";
import MUITable from "Components/UI/Tables/MUITable";
import useTableOptions from "Hooks/useTableOptions";

//MUI
import IconButton from "@mui/material/IconButton";
import LinkIcon from "@mui/icons-material/Link";
import {
  getAccountCharts,
  getBooks,
  getClients,
  getShortInfoClients,
} from "API/swesum";
import dayjs from "dayjs";
import DateWithClear from "Components/UI/Form/DateWithClear";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
} from "@mui/material";

const ShowTypes = [
  { id: 0, name: "Alla" },
  { id: 1, name: "Öppna" },
];

const createTableRows = (books) => {
  return books?.map((i) => {
    return [
      i.Book.Id,
      i.AccountChart.Title,
      i.AccountChart.AccountNumber,
      price2StringPrice(i.Book.DebetAmount === null ? 0 : i.Book.DebetAmount),
      price2StringPrice(i.Book.KreditAmount === null ? 0 : i.Book.KreditAmount),
      i.Amount,
      i.ClientNumber,
      { label: i.ExternalInvoiceNo, id: i.Book.InvoiceId },
      i.Book.InvoiceId,
      i.Book.Description,
      i.Book.BookDate,
      i.Book.FileId,
      getParseStatusLabelById(i.Book.ParseStatus),
      cutStrAtAndAddDots(i.Book.ParseMessage, 45),
      getSystemStatusLabelById(i.Book.SystemStatus),
      i.Book.CreateTime,
    ];
  });
};

const columnsToHide = ["Faktura ID"];

export default function BooksTable() {
  const { handleColumnShow, isColDisplayed, isNew } =
    useTableOptions("BooksTable");
  const location = useLocation();

  const [show, setShow] = useState(0);
  const [selectedClientIds, setSelectedClientIds] = useState([-1]);
  const [accounts, setAccounts] = useState([]);
  const [clients, setClients] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(0); // These are going to change when we fix pagination
  const [pageSize, setPageSize] = useState(0); // These are going to change when we fix pagination
  const [selectedAccountIds, setSelectedAccountIds] = useState([-1]);
  const [rows, setRows] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const asyncFetchBooks = async () => {
    const body = {
      Show: ShowTypes.find((st) => st.id === show).name,
      Accounts: selectedAccountIds.find((val) => val === -1)
        ? []
        : selectedAccountIds,
      Clients: selectedClientIds.find((val) => val === -1)
        ? []
        : selectedClientIds,
      FromDate: dayjs(fromDate).isValid()
        ? dayjs(fromDate).format("YYYY-MM-DD")
        : "",
      ToDate: dayjs(toDate).isValid() ? dayjs(toDate).format("YYYY-MM-DD") : "",
      Page: currentPage,
      PageSize: pageSize,
    };

    const books = await getBooks(body);
    const rows = createTableRows(books);
    setRows(rows);
  };

  useEffect(() => {
    const asyncFetchClients = async () => {
      const res = await getShortInfoClients();
      setClients([...res]);
    };

    const asyncFetchAccountCharts = async () => {
      const res = await getAccountCharts();
      const queryParams = new URLSearchParams(location.search);
      const clientId = queryParams.get("clientId");
      if (clientId) {
        setSelectedAccountIds([
          res.find((ac) => ac.AccountNumber === "1340-D").Id,
        ]);
      }
      setAccounts([...res]);
    };

    async function initialize() {
      await asyncFetchAccountCharts();
      await asyncFetchClients();

      const queryParams = new URLSearchParams(location.search);
      const accountId = queryParams.get("accountId");
      const date = queryParams.get("date");
      const clientId = queryParams.get("clientId");
      const showParam = queryParams.get("show");

      if (accountId) {
        setSelectedAccountIds([Number(accountId)]);
      }

      if (clientId) {
        setSelectedClientIds([Number(clientId)]);
      }

      if (date) {
        setToDate(date);
      }

      setShow(showParam === "open" ? 1 : 0);

      setIsInitialized(true);
    }

    initialize();
  }, [location]);

  useEffect(() => {
    if (isInitialized) {
      asyncFetchBooks();
    }
  }, [
    location,
    toDate,
    fromDate,
    selectedAccountIds,
    show,
    selectedClientIds,
    isInitialized,
  ]);

  useEffect(() => {
    if (isNew) {
      for (const col of columnsToHide) {
        handleColumnShow(col, "remove");
      }
    }
  }, [isNew]);

  const columns = [
    {
      name: "",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex) => {
          return (
            <IconButton
              component={Link}
              to={`${rows[dataIndex][0]}`}
              aria-label="edit"
              size="small"
            >
              <LinkIcon sx={{ p: 0, m: 0 }} />
            </IconButton>
          );
        },
      },
    },
    {
      name: "ID",
      options: {
        display: isColDisplayed("ID"),
        filter: false,
      },
    },
    {
      name: "Konto",
      options: {
        display: isColDisplayed("Konto"),
        filter: false,
      },
    },
    {
      name: "Kontonr",
      options: {
        display: isColDisplayed("Kontonr"),
        filter: false,
      },
    },
    {
      name: "Debet",
      options: {
        display: isColDisplayed("Debet"),
        filter: false,
      },
    },
    {
      name: "Kredit",
      options: {
        display: isColDisplayed("Kredit"),
        filter: false,
      },
    },
    {
      name: "Belopp",
      options: {
        display: isColDisplayed("Belopp"),
        filter: false,
      },
    },
    {
      name: "Klient",
      options: {
        display: isColDisplayed("Klient"),
        filter: false,
      },
    },
    {
      name: "Fakturanummer",
      options: {
        display: isColDisplayed("Fakturanummer"),
        filter: false,
        customBodyRender: (value) => {
          return <Link to={`/invoices/${value.id}`}>{value.label}</Link>;
        },
      },
    },
    {
      name: "Faktura ID",
      options: {
        filter: false,
        display: isColDisplayed("Faktura ID"),
        customBodyRender: (value) => {
          return <Link to={`/invoices/${value}`}>{value}</Link>;
        },
      },
    },
    {
      name: "Beskrivning",
      options: {
        display: isColDisplayed("Beskrivning"),
        filter: false,
      },
    },
    {
      name: "Bokföringsdatum",
      options: {
        display: isColDisplayed("Bokföringsdatum"),
        filter: false,
      },
    },
    {
      name: "Bokföringsfil",
      options: {
        filter: false,
        display: isColDisplayed("Bokföringsfil"),
        customBodyRender: (value) => {
          return <Link to={`/files/${value}`}>{value}</Link>;
        },
      },
    },

    {
      name: "Kontrollstatus",
      options: {
        filter: true,
        display: isColDisplayed("Kontrollstatus"),
        sort: true,
        customBodyRender: (value) => {
          return (
            <CommonChip
              labelName={value}
              color={getParseStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "Kontrollmeddelande",
      options: {
        filter: false,
        display: isColDisplayed("Kontrollmeddelande"),
      },
    },
    {
      name: "Status",
      options: {
        filter: true,
        display: isColDisplayed("Status"),
        sort: true,
        customBodyRender: (value) => {
          return (
            <CommonChip
              labelName={value}
              color={getSystemStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "Skapad",
      options: {
        filter: false,
        display: isColDisplayed("Skapad"),
      },
    },
  ];

  const options = {
    selectableRows: "none",
    selectableRowsHideCheckboxes: true,
    filterType: "dropdown",
    responsive: "standard",
    downloadOptions: {
      separator: ";",
      filename: "all_books.csv",
    },
    rowsPerPage: 30,
    rowsPerPageOptions: [10, 30, 100],
    onDownload: (buildHead, buildBody, columns, data) => {
      const newData = data.map((d1) => {
        const newD1 = d1.data.map((d2) => {
          if (d2 && typeof d2 === "object" && d2.label !== null) {
            return d2.label;
          }
          return d2;
        });
        return { ...d1, data: newD1 };
      });

      return "\uFEFF" + buildHead(columns) + buildBody(newData);
    },
  };

  return (
    <>
      <Paper
        sx={{
          padding: "10px",
          marginBottom: "10px",
        }}
        elevation={4}
      >
        <Grid item container spacing={4} marginBottom={2}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl
              fullWidth
              margin="dense"
              sx={{ textAlign: "left", background: "white" }}
            >
              <InputLabel>Visa</InputLabel>
              <Select
                name="show"
                label="Visa"
                value={show}
                onChange={(e) => setShow(e.target.value)}
              >
                {ShowTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid item container spacing={4}>
          <Grid item xs={12} sm={6} md={2}>
            <DateWithClear
              label="Från datum"
              value={dayjs(fromDate)}
              onChange={(newVal) =>
                setFromDate(dayjs(newVal).format("YYYY-MM-DD"))
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DateWithClear
              label="Till datum"
              value={dayjs(toDate)}
              onChange={(newVal) =>
                setToDate(dayjs(newVal).format("YYYY-MM-DD"))
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl margin="dense" fullWidth sx={{ textAlign: "left" }}>
              <InputLabel>Kontonummer</InputLabel>
              <Select
                multiple
                name="AccountId"
                value={selectedAccountIds}
                onChange={(e) => {
                  if (
                    e.target.value.find((val) => val === -1) &&
                    selectedAccountIds.find((val) => val === -1) &&
                    e.target.value.length > 1
                  ) {
                    const newVals = e.target.value.filter((val) => val !== -1);
                    setSelectedAccountIds(newVals);
                  } else if (e.target.value.find((val) => val === -1)) {
                    setSelectedAccountIds([-1]);
                  } else {
                    setSelectedAccountIds(e.target.value);
                  }
                }}
                label="Kontonummer"
                size="small"
              >
                <MenuItem key={-1} value={-1}>
                  {"Alla"}
                </MenuItem>
                {accounts
                  ?.sort((a, b) =>
                    a.AccountNumber.localeCompare(b.AccountNumber)
                  )
                  .map((acc) => (
                    <MenuItem key={Number(acc.Id)} value={Number(acc.Id)}>
                      {acc.AccountNumber}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl margin="dense" fullWidth sx={{ textAlign: "left" }}>
              <InputLabel>Klient</InputLabel>
              <Select
                multiple
                name="klient"
                value={selectedClientIds}
                onChange={(e) => {
                  if (
                    e.target.value.find((val) => val === -1) &&
                    selectedClientIds.find((val) => val === -1) &&
                    e.target.value.length > 1
                  ) {
                    const newVals = e.target.value.filter((val) => val !== -1);
                    setSelectedClientIds(newVals);
                  } else if (e.target.value.find((val) => val === -1)) {
                    setSelectedClientIds([-1]);
                  } else {
                    setSelectedClientIds(e.target.value);
                  }
                }}
                label="Klient"
                size="small"
              >
                <MenuItem key={-1} value={-1}>
                  {"Alla"}
                </MenuItem>
                {clients
                  ?.sort((a, b) => a.Title.localeCompare(b.Title))
                  .map((client) => (
                    <MenuItem key={Number(client.Id)} value={Number(client.Id)}>
                      {client.Title}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      <MUITable options={options} title={""} data={rows} columns={columns} />
    </>
  );
}
